import React from "react";
import { useFormContext, Controller, useFieldArray } from "react-hook-form";
import { Input, Button, Card, CardBody, Switch, Progress } from "@heroui/react";
import { Icon } from "@iconify/react";
import { TRequest } from "@shared/entities";
import { TDocument, TDocumentType } from "@shared/entities/document";
import { firebaseApi } from "src/firebase-api";
import { getDownloadURL } from "firebase/storage";

// Mock document upload component for transporter ID
const TransporterDocumentUpload: React.FC<{
	fileName?: string;
	folder: string;
	type?: TDocumentType;
	onChange: (file: TDocument | null) => void;
	error?: string;
	value?: TDocument;
	isDisabled?: boolean;
}> = ({ onChange, error, value, folder, type = "ID", fileName, isDisabled }) => {
	console.log("value", value, folder);

	const [isUploading, setIsUploading] = React.useState(false);
	const [uploadProgress, setUploadProgress] = React.useState(0);
	const [isRemoving, setIsRemoving] = React.useState(false);

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			setIsUploading(true);
			// setValue(file);
			setUploadProgress(0);

			// Simulate file upload with progress
			simulateFileUpload(file, (progress) => {
				setUploadProgress(progress);
			});
		}
	};

	// Simulate file upload with progress
	const simulateFileUpload = async (file: File, onProgress: (progress: number) => void) => {
		const extension = file.name?.split(".").at(-1);

		const name = fileName ? `${fileName}.${extension}` : file.name;

		const task = firebaseApi.storage.uploadFileResumable({
			file,
			folder: folder,
			name: name,
		});
		task.on(
			"state_changed",
			(snapshot) => {
				const progressPercent = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
				console.log("progressPercent", progressPercent);

				onProgress(Math.round(progressPercent));
			},
			(err) => {
				console.log("ERRR", err.message);
			},
			async () => {
				const downloadURL = await getDownloadURL(task.snapshot.ref);
				console.log("downloadURL", downloadURL);

				setIsUploading(false);

				onChange({
					createdAt: Date.now(),
					extension: extension as TDocument["extension"],
					fileType: task.snapshot.metadata.contentType as TDocument["fileType"],
					type: type,
					name: name,
					size: task.snapshot.metadata.size,
					url: downloadURL,
					path: `${folder}/${name}`,
				});
			}
		);
	};

	const handleRemoveFile = async () => {
		if (!value?.path) return;

		setIsRemoving(true);
		// setValue(undefined);
		try {
			// Simulate API call to remove file
			await firebaseApi.storage.remove(value?.path);
			onChange(null as any);
		} catch (error) {
			console.error("Error removing file:", error);
			// Handle removal error
			onChange(null as any);
		} finally {
			setIsRemoving(false);
		}
	};

	return (
		<Card
			className={`border-2 border-dashed ${
				error ? "border-danger" : isUploading ? "border-primary" : "border-default-200"
			} mt-2`}
		>
			<CardBody className="p-3">
				<div className="flex flex-col items-center justify-center text-center">
					{isUploading && (
						<div className="w-full mb-3">
							<div className="flex items-center justify-between mb-1">
								<span className="text-xs text-default-500">מעלה קובץ...</span>
								<span className="text-xs font-medium">{uploadProgress}%</span>
							</div>
							<Progress
								aria-label="Uploading..."
								value={uploadProgress}
								color="primary"
								size="sm"
								className="w-full"
								showValueLabel={false}
							/>
						</div>
					)}

					{!!value && !isUploading ? (
						<div className="flex items-center gap-2 p-2 bg-default-100 rounded-medium w-full">
							<div className="bg-primary-100 p-1 rounded-full">
								<Icon icon="lucide:file" className="h-4 w-4 text-primary" />
							</div>
							<span className="text-sm truncate flex-1">{fileName}</span>
							<Button
								aria-label="הסר קובץ"
								isIconOnly
								size="sm"
								variant="light"
								color="danger"
								isLoading={isRemoving}
								onPress={handleRemoveFile}
								isDisabled={isRemoving}
							>
								<Icon icon="lucide:x" className="h-4 w-4" />
							</Button>
						</div>
					) : !isUploading ? (
						<div className="w-full">
							<label className="cursor-pointer">
								<div className="flex items-center justify-center gap-2 p-3 bg-default-100 hover:bg-default-200 transition-colors rounded-medium w-full">
									<Icon icon="lucide:upload" className="h-5 w-5 text-default-600" />
									<span className="text-sm">העלה צילום ת.ז של המשנע</span>
								</div>
								<input
									type="file"
									className="hidden"
									accept=".pdf,.jpg,.jpeg,.png"
									onChange={handleFileChange}
									disabled={isUploading || isRemoving || isDisabled}
								/>
							</label>
							<p className="text-default-400 text-xs mt-2">פורמטים מותרים: PDF, JPG, PNG</p>
						</div>
					) : null}

					{error && <p className="text-danger text-sm mt-2">{error}</p>}
				</div>
			</CardBody>
		</Card>
	);
};

export const TransportersForm: React.FC = () => {
	const {
		control,
		formState: { errors },
		setValue,
		watch,
	} = useFormContext<TRequest>();
	const hasTransporters = watch("medicalQuestionnaire.hasTransporters");

	const {
		fields: transporters,
		append,
		remove,
	} = useFieldArray({
		control,
		name: "transporters",
	});

	console.log("tr", transporters);

	const request = watch();
	console.log("request", request);

	console.log("errors", errors);

	// Ensure transporters array is initialized when hasTransporters is true
	React.useEffect(() => {
		if (hasTransporters) {
			console.log("hasTransporters", hasTransporters);

			setValue(
				"transporters",
				request.transporters ?? [
					{
						document: undefined as any,
						fullName: "",
						idNumber: "",
					},
				]
			);
		} else {
			setValue("transporters", []);
		}
	}, [hasTransporters, setValue]);

	return (
		<div className="space-y-6">
			<div className="flex items-center gap-3 mb-4">
				<div className="bg-primary-100 p-2 rounded-full">
					<Icon icon="lucide:truck" className="h-6 w-6 text-primary" />
				</div>
				<div>
					<h2 className="text-xl font-semibold">פרטי משנעים</h2>
					<p className="text-default-500 text-sm">הוסף פרטי משנעים מורשים</p>
				</div>
			</div>

			<div className="flex items-center justify-between p-4 bg-content2 rounded-medium">
				<div className="flex items-center gap-3">
					<Icon icon="lucide:truck" className="h-5 w-5 text-primary" />
					<span className="font-medium">האם יש לך משנעים מורשים?</span>
				</div>
				<Controller
					name="medicalQuestionnaire.hasTransporters"
					control={control}
					render={({ field }) => (
						<Switch
							isSelected={field.value}
							onValueChange={field.onChange}
							color="primary"
							size="sm"
						/>
					)}
				/>
			</div>

			{!!hasTransporters && (
				<div className="space-y-4">
					{transporters.map((transporter, index) => {
						console.log("transporter", transporter);

						return (
							<Card key={transporter.id} className="relative">
								<CardBody>
									<div className="flex justify-between items-center mb-3">
										<h3 className="font-medium">משנע {index + 1}</h3>
										{transporters.length > 1 && (
											<Button
												aria-label="הסר משנע"
												isIconOnly
												size="sm"
												color="danger"
												variant="light"
												onPress={() => {
													remove(index);
												}}
											>
												<Icon icon="lucide:trash-2" className="h-4 w-4" />
											</Button>
										)}
									</div>

									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<Controller
											name={`transporters.${index}.fullName`}
											control={control}
											render={({ field }) => (
												<Input
													{...field}
													label="שם מלא"
													placeholder="הכנס שם מלא של המשנע"
													isRequired
													isInvalid={!!errors?.transporters?.[index]?.fullName}
													errorMessage={
														errors?.transporters?.[index]?.fullName?.message
													}
													startContent={
														<Icon
															icon="lucide:user"
															className="h-4 w-4 text-default-400"
														/>
													}
												/>
											)}
										/>

										<Controller
											name={`transporters.${index}.idNumber`}
											control={control}
											render={({ field }) => (
												<Input
													{...field}
													label="מספר תעודת זהות"
													placeholder="הכנס מספר תעודת זהות"
													isRequired
													isInvalid={!!errors?.transporters?.[index]?.idNumber}
													errorMessage={
														errors?.transporters?.[index]?.idNumber?.message
													}
													startContent={
														<Icon
															icon="lucide:id-card"
															className="h-4 w-4 text-default-400"
														/>
													}
													// onChange={(e) => {
													// 	// field.onChange(e.target.value);
													// 	// updateDocumentIdNumber(index, e.target.value);
													// }}
												/>
											)}
										/>
									</div>

									<div className="mt-4">
										<p className="text-sm font-medium mb-2">צילום תעודת זהות</p>
										<Controller
											name={`transporters.${index}.document`}
											control={control}
											render={({ field }) => (
												<TransporterDocumentUpload
													onChange={field.onChange}
													value={field.value}
													error={errors.transporters?.[index]?.document?.message}
													fileName={"ID"}
													folder={`requests/${request.id}/transporters/${index}`}
													isDisabled={!request.transporters?.[index].idNumber}
												/>
											)}
										/>
									</div>
								</CardBody>
							</Card>
						);
					})}

					{transporters.length < 3 && (
						<Button
							size="sm"
							color="primary"
							variant="flat"
							startContent={<Icon icon="lucide:plus" className="h-4 w-4" />}
							onPress={() => {
								if (transporters.length < 3) {
									append({ idNumber: "", fullName: "", document: undefined as any });
								}
							}}
							className="w-full"
						>
							הוסף משנע נוסף
						</Button>
					)}

					{transporters.length >= 3 && (
						<div className="text-center text-default-500 text-sm">
							ניתן להוסיף עד 3 משנעים
						</div>
					)}
				</div>
			)}

			<div className="p-4 bg-info-50 border border-info-200 rounded-medium">
				<div className="flex items-start gap-3">
					<Icon icon="lucide:info" className="h-5 w-5 text-primary mt-0.5" />
					<div>
						<h3 className="font-medium text-primary-700 mb-1">מידע על משנעים</h3>
						<p className="text-sm text-primary-700">
							משנע הוא אדם המורשה להחזיק ולשנע את הקנאביס הרפואי עבור המטופל. יש לצרף צילום
							תעודת זהות של כל משנע. המשנע חייב להיות מעל גיל 18 וללא עבר פלילי.
						</p>
					</div>
				</div>
			</div>
		</div>
	);
};
